import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  Tag,
  Space,
  Dropdown,
  Modal,
  message,
  Form,
  Row,
  Col,
  Statistic,
  Typography,
  Divider,
  Badge,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { RootState, AppDispatch } from '../store';
import { fetchOrders, createOrder } from '../store/slices/paymentSlice';
import { setFilters, clearFilters, toggleOrderSelection } from '../store/slices/orderSlice';
import { Order, OrderStatus } from '../types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const { orders } = useSelector((state: RootState) => state.payment);
  const { selectedOrders, filters } = useSelector((state: RootState) => state.order);
  
  const [searchForm] = Form.useForm();
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();

  useEffect(() => {
    // 加载订单数据
    dispatch(fetchOrders({ 
      page: 1, 
      limit: 20, 
      filters 
    }));
  }, [dispatch, filters]);

  const handleSearch = (values: any) => {
    dispatch(setFilters(values));
  };

  const handleReset = () => {
    searchForm.resetFields();
    dispatch(clearFilters());
  };

  const handleCreateOrder = async (values: any) => {
    try {
      await dispatch(createOrder(values));
      message.success('订单创建成功');
      setCreateModalVisible(false);
      createForm.resetFields();
      // 重新加载订单列表
      dispatch(fetchOrders({ page: 1, limit: 20, filters }));
    } catch (error) {
      message.error('订单创建失败');
    }
  };

  const handleViewOrder = (record: Order) => {
    navigate(`/orders/${record.id}`);
  };

  const handleEditOrder = (record: Order) => {
    // 编辑订单逻辑
    Modal.info({
      title: '编辑订单',
      content: '订单编辑功能开发中...',
    });
  };

  const handleDeleteOrder = (record: Order) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除订单 ${record.id} 吗？`,
      onOk: () => {
        message.success('订单删除成功');
      },
    });
  };

  const handleBulkAction = (action: string) => {
    if (selectedOrders.length === 0) {
      message.warning('请先选择订单');
      return;
    }
    
    Modal.confirm({
      title: `批量${action}`,
      content: `确定要对选中的 ${selectedOrders.length} 个订单执行${action}操作吗？`,
      onOk: () => {
        message.success(`批量${action}成功`);
      },
    });
  };

  const statusConfig = {
    'pending': { color: 'processing', text: '待处理' },
    'confirmed': { color: 'success', text: '已确认' },
    'processing': { color: 'processing', text: '处理中' },
    'shipped': { color: 'warning', text: '已发货' },
    'delivered': { color: 'success', text: '已送达' },
    'cancelled': { color: 'error', text: '已取消' },
    'refunded': { color: 'default', text: '已退款' },
  };

  const columns = [
    {
      title: '订单ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (text: string) => (
        <Button 
          type="link" 
          onClick={() => handleViewOrder({ id: text } as Order)}
        >
          #{text.substring(0, 8)}
        </Button>
      ),
    },
    {
      title: '客户信息',
      dataIndex: 'customer',
      key: 'customer',
      width: 150,
      render: (customer: any) => (
        <div>
          <div>{customer?.name || '匿名客户'}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {customer?.email}
          </Text>
        </div>
      ),
    },
    {
      title: '商品',
      dataIndex: 'items',
      key: 'items',
      width: 200,
      render: (items: any[]) => (
        <div>
          {items?.slice(0, 2).map((item, index) => (
            <div key={index} style={{ fontSize: '12px' }}>
              {item.name} x{item.quantity}
            </div>
          ))}
          {items?.length > 2 && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              +{items.length - 2} 更多商品
            </Text>
          )}
        </div>
      ),
    },
    {
      title: '金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 100,
      sorter: true,
      render: (amount: number) => (
        <Text strong>¥{amount?.toFixed(2) || '0.00'}</Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      filters: Object.entries(statusConfig).map(([key, config]) => ({
        text: config.text,
        value: key,
      })),
      render: (status: OrderStatus) => {
        const config = statusConfig[status] || statusConfig.pending;
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '支付状态',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 100,
      render: (status: string) => {
        const paymentConfig = {
          'paid': { color: 'success', text: '已支付' },
          'pending': { color: 'processing', text: '待支付' },
          'failed': { color: 'error', text: '支付失败' },
          'refunded': { color: 'default', text: '已退款' },
        };
        const config = paymentConfig[status as keyof typeof paymentConfig] || paymentConfig.pending;
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: true,
      render: (date: string) => (
        <div>
          <div>{new Date(date).toLocaleDateString()}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {new Date(date).toLocaleTimeString()}
          </Text>
        </div>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: any, record: Order) => {
        const actionItems = [
          {
            key: 'view',
            label: '查看详情',
            icon: <EyeOutlined />,
            onClick: () => handleViewOrder(record),
          },
          {
            key: 'edit',
            label: '编辑订单',
            icon: <EditOutlined />,
            onClick: () => handleEditOrder(record),
          },
          {
            key: 'delete',
            label: '删除订单',
            icon: <DeleteOutlined />,
            onClick: () => handleDeleteOrder(record),
            danger: true,
          },
        ];

        return (
          <Space>
            <Button 
              type="link" 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => handleViewOrder(record)}
            >
              查看
            </Button>
            <Dropdown menu={{ items: actionItems }} trigger={['click']}>
              <Button type="link" size="small" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedOrders,
    onChange: (selectedRowKeys: React.Key[]) => {
      // 更新选中的订单
      selectedRowKeys.forEach(key => {
        dispatch(toggleOrderSelection(key as string));
      });
    },
  };

  return (
    <div className="orders-page">
      {/* 页面头部 */}
      <div className="page-header">
        <div className="header-content">
          <Title level={2}>订单管理</Title>
          <Text type="secondary">管理您的所有订单</Text>
        </div>
        <div className="header-actions">
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
              刷新
            </Button>
            <Button icon={<ExportOutlined />}>
              导出
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              创建订单
            </Button>
          </Space>
        </div>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="总订单"
              value={orders.pagination?.total || 0}
              valueStyle={{ color: '#2F6BFF' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="待处理"
              value={25}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="已完成"
              value={156}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="总金额"
              value={125680.50}
              prefix="¥"
              precision={2}
              valueStyle={{ color: '#2F6BFF' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 搜索表单 */}
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 0 }}
        >
          <Form.Item name="search" style={{ marginBottom: 0 }}>
            <Input
              placeholder="搜索订单ID、客户姓名"
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item name="status" style={{ marginBottom: 0 }}>
            <Select placeholder="订单状态" style={{ width: 120 }} allowClear>
              {Object.entries(statusConfig).map(([key, config]) => (
                <Option key={key} value={key}>{config.text}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="payment_status" style={{ marginBottom: 0 }}>
            <Select placeholder="支付状态" style={{ width: 120 }} allowClear>
              <Option value="paid">已支付</Option>
              <Option value="pending">待支付</Option>
              <Option value="failed">支付失败</Option>
              <Option value="refunded">已退款</Option>
            </Select>
          </Form.Item>
          <Form.Item name="date_range" style={{ marginBottom: 0 }}>
            <RangePicker placeholder={['开始日期', '结束日期']} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 批量操作 */}
      {selectedOrders.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <Space>
            <Text>已选择 {selectedOrders.length} 个订单</Text>
            <Divider type="vertical" />
            <Button size="small" onClick={() => handleBulkAction('确认')}>
              批量确认
            </Button>
            <Button size="small" onClick={() => handleBulkAction('发货')}>
              批量发货
            </Button>
            <Button size="small" onClick={() => handleBulkAction('取消')}>
              批量取消
            </Button>
            <Button size="small" onClick={() => handleBulkAction('导出')}>
              批量导出
            </Button>
          </Space>
        </Card>
      )}

      {/* 订单表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={orders.data || []}
          rowKey="id"
          loading={orders.loading}
          locale={{ emptyText: '暂无数据' }}
          rowSelection={rowSelection}
          pagination={{
            current: orders.pagination?.current_page || 1,
            pageSize: orders.pagination?.per_page || 20,
            total: orders.pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 创建订单模态框 */}
      <Modal
        title="创建订单"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateOrder}
        >
          <Form.Item
            name="customer_email"
            label="客户邮箱"
            rules={[
              { required: true, message: '请输入客户邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="customer@example.com" />
          </Form.Item>
          
          <Form.Item
            name="amount"
            label="订单金额"
            rules={[{ required: true, message: '请输入订单金额' }]}
          >
            <Input 
              type="number" 
              placeholder="0.00" 
              prefix="¥"
              step="0.01"
            />
          </Form.Item>
          
          <Form.Item
            name="currency"
            label="货币"
            initialValue="CNY"
          >
            <Select>
              <Option value="CNY">人民币 (CNY)</Option>
              <Option value="USD">美元 (USD)</Option>
              <Option value="EUR">欧元 (EUR)</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="description"
            label="订单描述"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="订单描述信息..."
            />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCreateModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                创建订单
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OrdersPage; 